import { combineReducers, configureStore, createListenerMiddleware, isAnyOf } from "@reduxjs/toolkit";
import { createLogger } from "@/utils/logger";
import authReducer, { setAuthenticated } from "./slices/auth";
import rentalDatesReducer from "./slices/rentalDates";
import socketReducer from "./slices/socket";
import cartCoverageReducer from "./slices/cartCoverage";
import supportChatReducer from "./slices/supportChat";
import vendorTermsReducer from "./slices/vendorTerms";
import { productApi } from "./api/productApi";
import { listingsApi } from "./api/listingsApi";
import { vendorApi } from "./api/vendorApi";
import { inventoryItemsApi } from "./api/inventoryItemsApi";
import { RECEIPT_TAGS_GEAR_CHANGES, receiptsApi } from "./api/receiptsApi";
import { ordersApi } from "./api/ordersApi";
import { PROFILE_TAGS, userApi } from "./api/userApi";
import { homeApi } from "./api/homeApi";
import { cartApi } from "./api/cartApi";
import { serviceAreaApi } from "./api/serviceAreaApi";
import { authApi } from "./api/authApi";
import { paymentApi } from "./api/paymentApi";
import { conversationApi } from "./api/conversationApi";
import { notificationApi } from "./api/notificationApi";
import { supportApi } from "./api/supportApi";
import { announcementApi } from "./api/announcementApi";
import { stagingGateApi } from "./api/stagingGateApi";
import { salesTaxApi } from "./api/salesTaxApi";

const rootReducer = combineReducers({
  auth: authReducer,
  rentalDates: rentalDatesReducer,
  socket: socketReducer,
  cartCoverage: cartCoverageReducer,
  supportChat: supportChatReducer,
  vendorTerms: vendorTermsReducer,
  [productApi.reducerPath]: productApi.reducer,
  [listingsApi.reducerPath]: listingsApi.reducer,
  [vendorApi.reducerPath]: vendorApi.reducer,
  [inventoryItemsApi.reducerPath]: inventoryItemsApi.reducer,
  [receiptsApi.reducerPath]: receiptsApi.reducer,
  [ordersApi.reducerPath]: ordersApi.reducer,
  [userApi.reducerPath]: userApi.reducer,
  [homeApi.reducerPath]: homeApi.reducer,
  [cartApi.reducerPath]: cartApi.reducer,
  [serviceAreaApi.reducerPath]: serviceAreaApi.reducer,
  [authApi.reducerPath]: authApi.reducer,
  [paymentApi.reducerPath]: paymentApi.reducer,
  [conversationApi.reducerPath]: conversationApi.reducer,
  [notificationApi.reducerPath]: notificationApi.reducer,
  [supportApi.reducerPath]: supportApi.reducer,
  [announcementApi.reducerPath]: announcementApi.reducer,
  [stagingGateApi.reducerPath]: stagingGateApi.reducer,
  [salesTaxApi.reducerPath]: salesTaxApi.reducer,
});

/**
 * Store-derived types. Use these everywhere selectors and dispatch appear:
 *
 *   import type { RootState, AppDispatch } from '@/redux/store'
 *   const cart = useAppSelector((s) => s.rentalDates.startDate)
 *
 * useAppSelector / useAppDispatch in @/redux/hooks are pre-typed with
 * these, so component authors get inference without needing to import
 * RootState directly in most cases.
 */
export type RootState = ReturnType<typeof rootReducer>;

const log = createLogger("store");

/**
 * The inventory and listing saves that change what the receipt pages show:
 * the gear needing a receipt, what the batch matches against the vendor's
 * gear, and the gear each receipt names. Only saves that reach the server;
 * a listing draft kept in the browser changes nothing there.
 */
const gearSaved = isAnyOf(
  inventoryItemsApi.endpoints.createInventoryItem.matchFulfilled,
  inventoryItemsApi.endpoints.updateInventoryItem.matchFulfilled,
  inventoryItemsApi.endpoints.deleteInventoryItem.matchFulfilled,
  listingsApi.endpoints.createListing.matchFulfilled,
  listingsApi.endpoints.updateListing.matchFulfilled,
  listingsApi.endpoints.deleteListing.matchFulfilled,
  listingsApi.endpoints.duplicateListing.matchFulfilled,
  listingsApi.endpoints.publishDrafts.matchFulfilled,
);

/**
 * The receipt pages keep their answers until something marks them stale
 * (receiptsApi), and a gear save lives in other slices, so the store marks
 * them here, in one place, when one succeeds: the pages read again only
 * after a change (docs/plans/receipts-redesign.md, Requests the receipt
 * pages send 8). One per store, as each store is a page load's or a test's.
 */
const receiptsFollowGearSaves = () => {
  const listener = createListenerMiddleware();
  listener.startListening({
    matcher: gearSaved,
    effect: (saved, api) => {
      log.debug({ save: saved.type }, "a gear save marked the receipt pages stale");
      api.dispatch(receiptsApi.util.invalidateTags([...RECEIPT_TAGS_GEAR_CHANGES]));
    },
  });
  return listener.middleware;
};

/** The email links that change the person: the signup link, and the link confirming a new address. */
const emailLinkLanded = isAnyOf(
  authApi.endpoints.verifyEmail.matchFulfilled,
  authApi.endpoints.confirmEmailChange.matchFulfilled,
);

/**
 * An email link opens a tab of its own, which reads the signed-in person's
 * profile as it boots, before the link lands. The PROFILE_CHANGED push the
 * link sends reaches the person's other tabs, but this tab's socket is not
 * up yet to hear it, and the profile lives in another slice than the
 * link's. So the store marks the profile stale here when a link succeeds,
 * and its pages show the change with no reload (Tyler, 2026-10-23: "it
 * doesn't show that its verified on store profile or account standing
 * until i hard refresh them").
 */
const profileFollowsEmailLinks = () => {
  const listener = createListenerMiddleware();
  listener.startListening({
    matcher: emailLinkLanded,
    effect: (landed, api) => {
      log.debug({ link: landed.type }, "an email link marked the profile stale");
      api.dispatch(userApi.util.invalidateTags([...PROFILE_TAGS]));
    },
  });
  return listener.middleware;
};

/**
 * A list's rows carry the viewer's own favorites, and a list outlives the page
 * that read it (listingsApi), so the store marks the lists stale when who is
 * looking changes: someone who signs in on the way back to a search sees their
 * hearts. A refreshed token says signed in again and changes nothing, so only
 * a change counts.
 */
const listsFollowTheViewer = () => {
  const listener = createListenerMiddleware<RootState>();
  listener.startListening({
    actionCreator: setAuthenticated,
    effect: (changed, api) => {
      if (changed.payload === api.getOriginalState().auth.isAuthenticated) return;
      log.debug({ isAuthenticated: changed.payload }, "who is looking changed; the listing lists are stale");
      api.dispatch(listingsApi.util.invalidateTags([{ type: "Listings", id: "LIST" }]));
    },
  });
  return listener.middleware;
};

/**
 * A store with every slice and API the app has. The app runs on one
 * (`store` below); a test builds its own, from the state it starts in, so
 * the test store cannot drift from the app's: a slice added here is in both.
 */
export const makeStore = (preloadedState?: Partial<RootState>) =>
  configureStore({
    reducer: rootReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().prepend(receiptsFollowGearSaves(), profileFollowsEmailLinks(), listsFollowTheViewer()).concat(
        productApi.middleware,
        listingsApi.middleware,
        vendorApi.middleware,
        inventoryItemsApi.middleware,
        receiptsApi.middleware,
        ordersApi.middleware,
        userApi.middleware,
        homeApi.middleware,
        cartApi.middleware,
        serviceAreaApi.middleware,
        authApi.middleware,
        paymentApi.middleware,
        conversationApi.middleware,
        notificationApi.middleware,
        supportApi.middleware,
        announcementApi.middleware,
        stagingGateApi.middleware,
        salesTaxApi.middleware,
      ),
  });

export const store = makeStore();

export type AppDispatch = typeof store.dispatch;
